/**
 * Formulários por tipo de bloco.
 *
 * Os avisos aqui não são enfeite — são a interface das decisões de segurança do sistema:
 *
 *  - **modo `page`** aparece atrás de um toggle com o aviso explícito de acesso total à página, e
 *    lembrando que ele só funciona nos domínios autorizados (o servidor nem entrega a config fora
 *    deles) e que o preview força sandbox;
 *  - **webhook no navegador** avisa que header é visível ao visitante e que o destino precisa
 *    liberar CORS — a causa nº 1 de "não funciona" nesse bloco;
 *  - **webhook no backend** é o único lugar onde segredo pode entrar, referenciado por
 *    `{{secret:nome}}`; o valor nunca é lido de volta pelo painel.
 */

import type {
  BackendWebhookBlockData,
  BrowserCodeBlockData,
  BrowserWebhookBlockData,
  ButtonsBlockData,
  ConditionBlockData,
  ConditionOperator,
  HeaderPair,
  InputBlockData,
  MessageBlockData,
} from '@merlin/flow-engine';
import { extractTokens } from '@merlin/flow-engine';
import { createOptionId } from '../../flow/mapping.js';
import { IconeMais } from '../icons.js';
import { Button, Field, GroupTitle, Input, Notice, Select, Switch, Textarea } from '../ui.js';

type Update<T> = (patch: Partial<T>) => void;

function TokenHint({ templates }: { templates: (string | undefined)[] }) {
  const tokens = [...new Set(templates.filter(Boolean).flatMap((t) => extractTokens(t as string)))];
  if (tokens.length === 0) return null;
  return (
    <span>
      Variáveis usadas: {tokens.map((token) => `{{${token}}}`).join(', ')}
    </span>
  );
}

function LabelField({ value, onChange }: { value: string | undefined; onChange(next: string): void }) {
  return (
    <Field label="Nome do bloco" hint="Só aparece no editor, para você se localizar no canvas.">
      <Input value={value ?? ''} onChange={(event) => onChange(event.target.value)} />
    </Field>
  );
}

export function MessageForm({ data, update }: { data: MessageBlockData; update: Update<MessageBlockData> }) {
  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />
      <Field
        label="Texto"
        hint={<TokenHint templates={[data.text]} />}
      >
        <Textarea
          rows={5}
          value={data.text}
          onChange={(event) => update({ text: event.target.value })}
          placeholder="Olá {{nome}}, tudo bem?"
        />
      </Field>
    </div>
  );
}

export function InputForm({ data, update }: { data: InputBlockData; update: Update<InputBlockData> }) {
  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />
      <Field label="Pergunta" hint={<TokenHint templates={[data.prompt]} />}>
        <Textarea rows={3} value={data.prompt} onChange={(event) => update({ prompt: event.target.value })} />
      </Field>
      <Field
        label="Salvar em"
        hint="Nome da variável. Use depois como {{nome_da_variavel}} em qualquer bloco."
      >
        <Input value={data.saveAs} onChange={(event) => update({ saveAs: event.target.value })} />
      </Field>
      <Field label="Tipo de resposta" hint="Define a validação e o teclado no celular.">
        <Select
          value={data.inputType}
          onChange={(event) => update({ inputType: event.target.value as InputBlockData['inputType'] })}
        >
          <option value="text">Texto</option>
          <option value="email">E-mail</option>
          <option value="phone">Telefone</option>
          <option value="number">Número</option>
        </Select>
      </Field>
      <Field label="Placeholder">
        <Input
          value={data.placeholder ?? ''}
          onChange={(event) => update({ placeholder: event.target.value })}
        />
      </Field>
    </div>
  );
}

/**
 * O id da opção **não aparece aqui**, e é de propósito.
 *
 * Ele é só a chave de fiação da aresta, nunca visível em outro lugar da interface — a porta no canvas
 * e o rótulo da aresta usam o texto do botão. Enquanto o campo existiu, ele foi confundido com o valor
 * a salvar, e a variável passou a trazer o texto do botão. Ver `createOptionId`.
 *
 * O campo de valor só aparece quando há `saveAs`: sem variável nada é salvo, então ele não faria nada
 * e seria só ruído numa coluna de 384px.
 */
export function ButtonsForm({
  data,
  update,
  onRemoveOption,
}: {
  data: ButtonsBlockData;
  update: Update<ButtonsBlockData>;
  onRemoveOption(optionId: string): void;
}) {
  const options = data.options ?? [];
  const salvaEm = data.saveAs?.trim();

  const patchOption = (index: number, patch: Partial<(typeof options)[number]>): void => {
    const next = options.map((option, i) => {
      if (i !== index) return option;
      const juntos = { ...option, ...patch };
      /*
       * Chave ausente e chave com `undefined` não são a mesma coisa fora do JS: o Firestore **recusa**
       * `undefined` e derruba o autosave com "erro ao salvar". Espalhar `{ value: undefined }` cria a
       * chave em vez de tirá-la, então a remoção precisa ser explícita.
       */
      if (juntos.value === undefined) delete juntos.value;
      return juntos;
    });
    update({ options: next });
  };

  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />
      <Field label="Pergunta" hint={<TokenHint templates={[data.prompt]} />}>
        <Textarea rows={3} value={data.prompt} onChange={(event) => update({ prompt: event.target.value })} />
      </Field>
      <Field
        label="Salvar escolha em"
        hint="Opcional. Guarda o valor da opção clicada — ou o texto do botão, quando ela não tem valor próprio."
      >
        <Input value={data.saveAs ?? ''} onChange={(event) => update({ saveAs: event.target.value })} />
      </Field>

      <div className="space-y-2">
        <GroupTitle>Opções</GroupTitle>
        {/* Uma linha no bloco, e não uma prévia repetida em cada opção. */}
        {salvaEm ? (
          <p className="text-xs leading-snug text-slate-500">
            Cada opção salva o valor abaixo em{' '}
            <code className="rounded bg-slate-100 px-1 text-slate-800">{salvaEm}</code>. Em branco,
            salva o texto do botão.
          </p>
        ) : null}

        {options.map((option, index) => (
          <div key={option.id} className="space-y-2 rounded-lg border border-slate-200 p-3">
            <div className="flex gap-2">
              <Input
                value={option.label}
                placeholder="Texto do botão"
                onChange={(event) => patchOption(index, { label: event.target.value })}
              />
              {/* Remove pelo store: a aresta que saía desta opção precisa ir junto. */}
              <Button
                variant="danger"
                disabled={options.length <= 1}
                onClick={() => onRemoveOption(option.id)}
              >
                Remover
              </Button>
            </div>

            {salvaEm ? (
              <Field label="Valor salvo">
                <Input
                  value={option.value ?? ''}
                  placeholder={option.label}
                  onChange={(event) =>
                    // String vazia é o mesmo que "não definido": com `value: ''` o engine para de
                    // cair no rótulo e a variável viria vazia.
                    patchOption(index, { value: event.target.value || undefined })
                  }
                />
              </Field>
            ) : null}
          </div>
        ))}

        <Button
          onClick={() =>
            update({
              options: [
                ...options,
                { id: createOptionId(), label: `Opção ${options.length + 1}` },
              ],
            })
          }
        >
          <IconeMais />
          Adicionar opção
        </Button>
      </div>
    </div>
  );
}

const OPERATORS: { value: ConditionOperator; label: string; needsValue: boolean }[] = [
  { value: 'eq', label: 'é igual a', needsValue: true },
  { value: 'neq', label: 'é diferente de', needsValue: true },
  { value: 'contains', label: 'contém', needsValue: true },
  { value: 'notContains', label: 'não contém', needsValue: true },
  { value: 'gt', label: 'é maior que', needsValue: true },
  { value: 'gte', label: 'é maior ou igual a', needsValue: true },
  { value: 'lt', label: 'é menor que', needsValue: true },
  { value: 'lte', label: 'é menor ou igual a', needsValue: true },
  { value: 'isEmpty', label: 'está vazia', needsValue: false },
  { value: 'isNotEmpty', label: 'não está vazia', needsValue: false },
];

export function ConditionForm({ data, update }: { data: ConditionBlockData; update: Update<ConditionBlockData> }) {
  const rules = data.rules ?? [];

  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />

      <Field label="Modo" hint="O modo visual cobre a maioria dos casos sem escrever sintaxe.">
        <Select
          value={data.mode}
          onChange={(event) => update({ mode: event.target.value as ConditionBlockData['mode'] })}
        >
          <option value="builder">Visual (regras)</option>
          <option value="expression">Expressão</option>
        </Select>
      </Field>

      {data.mode === 'builder' ? (
        <div className="space-y-2">
          <Field label="Combinar regras com">
            <Select
              value={data.combinator ?? 'and'}
              onChange={(event) => update({ combinator: event.target.value as 'and' | 'or' })}
            >
              <option value="and">E — todas precisam ser verdadeiras</option>
              <option value="or">OU — basta uma ser verdadeira</option>
            </Select>
          </Field>

          {rules.map((rule, index) => {
            const operator = OPERATORS.find((o) => o.value === rule.operator);
            return (
              <div key={index} className="space-y-2 rounded-lg border border-slate-200 p-3">
                <Input
                  value={rule.variable}
                  placeholder="variável"
                  onChange={(event) =>
                    update({
                      rules: rules.map((r, i) => (i === index ? { ...r, variable: event.target.value } : r)),
                    })
                  }
                  className="font-mono text-xs"
                />
                <Select
                  value={rule.operator}
                  onChange={(event) =>
                    update({
                      rules: rules.map((r, i) =>
                        i === index ? { ...r, operator: event.target.value as ConditionOperator } : r,
                      ),
                    })
                  }
                >
                  {OPERATORS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {operator?.needsValue ? (
                  <Input
                    value={rule.value ?? ''}
                    placeholder="valor de comparação"
                    onChange={(event) =>
                      update({
                        rules: rules.map((r, i) => (i === index ? { ...r, value: event.target.value } : r)),
                      })
                    }
                  />
                ) : null}
                <Button
                  variant="danger"
                  disabled={rules.length <= 1}
                  onClick={() => update({ rules: rules.filter((_, i) => i !== index) })}
                >
                  Remover regra
                </Button>
              </div>
            );
          })}
          <Button
            onClick={() => update({ rules: [...rules, { variable: '', operator: 'eq', value: '' }] })}
          >
            <IconeMais />
            Adicionar regra
          </Button>
        </div>
      ) : (
        <>
          <Field
            label="Expressão"
            hint="Operadores: == != > >= < <= contains notContains, com && || ! e parênteses. Não é JavaScript: nada de chamada de função."
          >
            <Textarea
              rows={3}
              value={data.expression ?? ''}
              onChange={(event) => update({ expression: event.target.value })}
              placeholder='plano == "pro" && idade >= 18'
            />
          </Field>
          <Notice tone="info">
            A expressão é avaliada por um interpretador restrito, não por <code>eval</code>. Isso é o
            que permite servi-la numa config pública sem virar execução de código arbitrário no
            navegador do visitante.
          </Notice>
        </>
      )}
    </div>
  );
}

export function BrowserCodeForm({
  data,
  update,
}: {
  data: BrowserCodeBlockData;
  update: Update<BrowserCodeBlockData>;
}) {
  const isPage = data.executionMode === 'page';

  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />

      <Switch
        checked={isPage}
        tone="warning"
        label="Rodar no contexto da página (modo page)"
        description={
          <>
            <strong>Este código terá acesso total à sua página</strong> — window, document, DOM e
            cookies do site. Equivale ao Custom HTML do GTM. Desligado, o código roda num Web Worker
            isolado, sem DOM e sem rede.
          </>
        }
        onChange={(next) => update({ executionMode: next ? 'page' : 'sandbox' })}
      />

      {isPage ? (
        <Notice tone="warn" title="O que sustenta a segurança do modo page">
          <p>
            É o vínculo com o domínio: o servidor não entrega a config para domínios fora da sua
            allowlist, então este código só roda nos sites que você registrou.
          </p>
          <p>
            No preview ele roda no sandbox — não vale executar código de terceiros no contexto do
            painel. E se o site tiver CSP sem <code>unsafe-eval</code>, o bloco falha com
            diagnóstico no console em vez de silenciosamente.
          </p>
        </Notice>
      ) : (
        <Notice tone="info" title="O que o sandbox garante">
          Web Worker sem DOM, e com <code>fetch</code>/<code>XMLHttpRequest</code> removidos — um
          worker de <code>blob:</code> herda a origem do site host, então sem isso ele poderia falar
          com o backend do próprio cliente. Para chamar API, use um bloco de webhook.
        </Notice>
      )}

      <Field
        label="Código"
        hint="`vars` traz as variáveis do fluxo. Use `return` para salvar um valor."
      >
        <Textarea rows={10} value={data.code} onChange={(event) => update({ code: event.target.value })} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Salvar retorno em">
          <Input value={data.saveAs ?? ''} onChange={(event) => update({ saveAs: event.target.value })} />
        </Field>
        <Field label="Timeout (ms)" hint={isPage ? 'No modo page, não interrompe laço síncrono.' : 'O worker é encerrado.'}>
          <Input
            type="number"
            value={data.timeoutMs ?? 5000}
            onChange={(event) => update({ timeoutMs: Number(event.target.value) })}
          />
        </Field>
      </div>
    </div>
  );
}

function HeadersEditor({
  headers,
  onChange,
}: {
  headers: HeaderPair[];
  onChange(next: HeaderPair[]): void;
}) {
  return (
    <div className="space-y-2">
      <GroupTitle>Headers</GroupTitle>
      {headers.map((header, index) => (
        <div key={index} className="flex gap-2">
          <Input
            value={header.key}
            placeholder="Nome"
            onChange={(event) =>
              onChange(headers.map((h, i) => (i === index ? { ...h, key: event.target.value } : h)))
            }
          />
          <Input
            value={header.value}
            placeholder="Valor"
            onChange={(event) =>
              onChange(headers.map((h, i) => (i === index ? { ...h, value: event.target.value } : h)))
            }
          />
          <Button variant="danger" onClick={() => onChange(headers.filter((_, i) => i !== index))}>
            ×
          </Button>
        </div>
      ))}
      <Button onClick={() => onChange([...headers, { key: '', value: '' }])}>Adicionar header</Button>
    </div>
  );
}

export function BrowserWebhookForm({
  data,
  update,
}: {
  data: BrowserWebhookBlockData;
  update: Update<BrowserWebhookBlockData>;
}) {
  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />

      <Notice tone="warn" title="Esta requisição sai do navegador do visitante">
        <p>
          Ela aparece no DevTools dele: <strong>qualquer header aqui é público</strong>. Para usar
          token ou chave, escolha o bloco de webhook no backend.
        </p>
        <p>
          O destino também precisa liberar CORS para o site do cliente
          (<code>Access-Control-Allow-Origin</code>), senão a chamada falha no navegador.
        </p>
      </Notice>

      <Field label="URL" hint={<TokenHint templates={[data.url]} />}>
        <Input value={data.url} onChange={(event) => update({ url: event.target.value })} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Método">
          <Select
            value={data.method}
            onChange={(event) => update({ method: event.target.value as BrowserWebhookBlockData['method'] })}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Content-Type">
          <Select
            value={data.contentType}
            onChange={(event) =>
              update({ contentType: event.target.value as BrowserWebhookBlockData['contentType'] })
            }
          >
            <option value="application/json">application/json</option>
            <option value="application/x-www-form-urlencoded">form-urlencoded</option>
            <option value="text/plain">text/plain</option>
          </Select>
        </Field>
      </div>

      <HeadersEditor headers={data.headers ?? []} onChange={(headers) => update({ headers })} />

      {data.method !== 'GET' ? (
        <Field label="Corpo" hint={<TokenHint templates={[data.body]} />}>
          <Textarea rows={5} value={data.body ?? ''} onChange={(event) => update({ body: event.target.value })} />
        </Field>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Salvar resposta em">
          <Input value={data.saveAs ?? ''} onChange={(event) => update({ saveAs: event.target.value })} />
        </Field>
        <Field label="Timeout (ms)">
          <Input
            type="number"
            value={data.timeoutMs ?? 10000}
            onChange={(event) => update({ timeoutMs: Number(event.target.value) })}
          />
        </Field>
      </div>
    </div>
  );
}

export function BackendWebhookForm({
  data,
  update,
  secretNames,
  outboundAllowlist,
}: {
  data: BackendWebhookBlockData;
  update: Update<BackendWebhookBlockData>;
  secretNames: string[];
  outboundAllowlist: string[];
}) {
  const secretRefs = data.secretRefs ?? [];
  const url = data.url ?? '';
  // Mesma regra do servidor: host com variável faz o destino depender do cliente, e aí a allowlist
  // explícita passa a ser obrigatória.
  const hostIsDynamic = /^[a-z]+:\/\/[^/]*\{\{/.test(url);

  return (
    <div className="space-y-4">
      <LabelField value={data.label} onChange={(label) => update({ label })} />

      <Notice tone="info" title="Executado no servidor">
        <p>
          O navegador manda só o id deste bloco. URL, headers, corpo e segredos ficam no servidor e
          <strong> não aparecem na config pública</strong> — nem para o visitante, nem no DevTools.
        </p>
      </Notice>

      <Field label="URL" hint={<TokenHint templates={[url]} />}>
        <Input value={url} onChange={(event) => update({ url: event.target.value })} />
      </Field>

      {hostIsDynamic ? (
        <Notice tone={outboundAllowlist.length ? 'warn' : 'danger'}>
          A URL tem variável na parte do host, então quem decide o destino passa a ser o visitante.
          {outboundAllowlist.length
            ? ' A allowlist de saída do webchat está preenchida, então só os domínios dela serão aceitos.'
            : ' Preencha a allowlist de saída na aba Domínios, senão o servidor vai recusar a execução.'}
        </Notice>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Método">
          <Select
            value={data.method ?? 'POST'}
            onChange={(event) => update({ method: event.target.value as BackendWebhookBlockData['method'] })}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Content-Type">
          <Select
            value={data.contentType ?? 'application/json'}
            onChange={(event) =>
              update({ contentType: event.target.value as BackendWebhookBlockData['contentType'] })
            }
          >
            <option value="application/json">application/json</option>
            <option value="application/x-www-form-urlencoded">form-urlencoded</option>
            <option value="text/plain">text/plain</option>
          </Select>
        </Field>
      </div>

      <HeadersEditor headers={data.headers ?? []} onChange={(headers) => update({ headers })} />

      {(data.method ?? 'POST') !== 'GET' ? (
        <Field label="Corpo" hint={<TokenHint templates={[data.body]} />}>
          <Textarea rows={5} value={data.body ?? ''} onChange={(event) => update({ body: event.target.value })} />
        </Field>
      ) : null}

      <div className="space-y-2">
        <GroupTitle>Segredos usados</GroupTitle>
        {secretNames.length === 0 ? (
          <p className="text-xs text-slate-500">
            Nenhum segredo cadastrado ainda. Crie na aba <strong>Segredos</strong> e ele aparece aqui.
          </p>
        ) : (
          <div className="space-y-2">
            {secretNames.map((name) => (
              <label key={name} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-slate-900"
                  checked={secretRefs.includes(name)}
                  onChange={(event) =>
                    update({
                      secretRefs: event.target.checked
                        ? [...secretRefs, name]
                        : secretRefs.filter((item) => item !== name),
                    })
                  }
                />
                <span>
                  <code className="rounded bg-slate-100 px-1">{`{{secret:${name}}}`}</code>
                </span>
              </label>
            ))}
          </div>
        )}
        <Notice tone="info">
          Marque o segredo e use <code>{'{{secret:nome}}'}</code> na URL, num header ou no corpo. Ele
          é substituído só no servidor, e o valor é removido da resposta antes de voltar ao navegador.
        </Notice>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Salvar resposta em">
          <Input value={data.saveAs ?? ''} onChange={(event) => update({ saveAs: event.target.value })} />
        </Field>
        <Field label="Timeout (ms)">
          <Input
            type="number"
            value={data.timeoutMs ?? 10000}
            onChange={(event) => update({ timeoutMs: Number(event.target.value) })}
          />
        </Field>
      </div>
    </div>
  );
}
